import { Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useGeneratorStore } from '../../stores/generatorStore';
import { defaultGenerationSettings } from '../GenerateTimetable/MockData';

const AutomationSettings: React.FC = () => {
  const { settings, updateSettings, resetSettings } = useGeneratorStore();
  const [saveMessage, setSaveMessage] = useState<string>('');

  const handleSave = () => {
    setSaveMessage('Settings saved successfully!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleReset = () => {
    resetSettings();
    setSaveMessage('Settings reset to defaults');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const getProfileDescription = (profileId: string): string => {
    const descriptions: Record<string, string> = {
      'profile-1':
        'Strict Compliance: Prioritizes hard rule adherence and minimal conflicts. Slower generation time but highest quality.',
      'profile-2':
        'Balanced Optimization: Balances quality and speed. Good for most schools with moderate constraints.',
      'profile-3':
        'Quick Generation: Fast generation with acceptable quality. Suitable for initial drafts and testing.',
      'profile-4':
        'Teacher-Focused: Optimizes for teacher wellbeing and preferences. May sacrifice some room efficiency.',
    };
    return descriptions[profileId] || '';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Automation Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Configure generation profiles and optimization parameters
        </Typography>
      </Box>

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {saveMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Selection */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Automation Profile
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Profile</InputLabel>
              <Select
                value={settings.profileId}
                label="Profile"
                onChange={(e) => updateSettings({ profileId: e.target.value })}
              >
                <MenuItem value="profile-1">Strict Compliance</MenuItem>
                <MenuItem value="profile-2">Balanced Optimization</MenuItem>
                <MenuItem value="profile-3">Quick Generation</MenuItem>
                <MenuItem value="profile-4">Teacher-Focused</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 2 }}>
              {getProfileDescription(settings.profileId)}
            </Alert>
          </Paper>
        </Grid>

        {/* Generation Parameters */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generation Parameters
            </Typography>

            <TextField
              fullWidth
              type="number"
              label="Max Generation Time (seconds)"
              value={settings.maxGenerationTime}
              onChange={(e) => updateSettings({ maxGenerationTime: Number.parseInt(e.target.value) })}
              margin="normal"
              helperText="Maximum time allowed for generation attempt"
            />

            <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowSoftRuleViolations}
                    onChange={(e) => updateSettings({ allowSoftRuleViolations: e.target.checked })}
                  />
                }
                label="Allow Soft Rule Violations"
              />
            </FormGroup>

            {settings.allowSoftRuleViolations && (
              <TextField
                fullWidth
                type="number"
                label="Max Soft Rule Violations"
                value={settings.maxSoftRuleViolations}
                onChange={(e) =>
                  updateSettings({ maxSoftRuleViolations: Number.parseInt(e.target.value) })
                }
                margin="normal"
                helperText="Maximum acceptable soft rule violations"
              />
            )}
          </Paper>
        </Grid>

        {/* Prioritization Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Optimization Priorities
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.prioritizeTeacherWellbeing}
                    onChange={(e) => updateSettings({ prioritizeTeacherWellbeing: e.target.checked })}
                  />
                }
                label="Prioritize Teacher Wellbeing"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                Minimize split days, gaps, and late finishes for teachers
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.prioritizeStudentWellbeing}
                    onChange={(e) => updateSettings({ prioritizeStudentWellbeing: e.target.checked })}
                  />
                }
                label="Prioritize Student Wellbeing"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                Balance subject distribution and avoid back-to-back difficult subjects
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.prioritizeRoomEfficiency}
                    onChange={(e) => updateSettings({ prioritizeRoomEfficiency: e.target.checked })}
                  />
                }
                label="Prioritize Room Efficiency"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                Maximize room utilization and minimize room changes
              </Typography>
            </FormGroup>
          </Paper>
        </Grid>

        {/* Weight Sliders (Placeholder - would be populated from automation profiles) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Advanced Weight Configuration
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Fine-tune optimization weights based on your selected profile. Changes here override profile
              defaults.
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography gutterBottom>Soft Rule Weight</Typography>
                <Slider
                  value={5}
                  min={0}
                  max={10}
                  marks
                  valueLabelDisplay="auto"
                  disabled
                />
                <Typography variant="caption" color="text.secondary">
                  Importance of soft rule compliance
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography gutterBottom>Preference Weight</Typography>
                <Slider
                  value={5}
                  min={0}
                  max={10}
                  marks
                  valueLabelDisplay="auto"
                  disabled
                />
                <Typography variant="caption" color="text.secondary">
                  Importance of general preferences
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography gutterBottom>Teacher Preference Weight</Typography>
                <Slider
                  value={5}
                  min={0}
                  max={10}
                  marks
                  valueLabelDisplay="auto"
                  disabled
                />
                <Typography variant="caption" color="text.secondary">
                  Importance of teacher-specific preferences
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleReset}>
              Reset to Defaults
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
              Save Settings
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AutomationSettings;
